/**
 * useSendMessage Hook
 *
 * All sends go through the sendMessage Cloud Function (moderation +
 * unread-count denormalization). Direct Firestore writes are no longer used.
 */

import { useState, useCallback } from 'react';
import toast from 'react-hot-toast';
import { doc, updateDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../../../../services/firebase';
import { useAuth } from '../../../../contexts/AuthContext';

interface SendMessageData {
  threadId: string;
  content: string;
}

export const useSendMessage = () => {
  const { user } = useAuth();
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const sendMessage = useCallback(async (data: SendMessageData): Promise<boolean> => {
    if (!user) return false;
    setIsSending(true);
    setError(null);

    try {
      const fn = httpsCallable<SendMessageData, { success: boolean; messageId: string }>(
        functions,
        'sendMessage'
      );
      await fn(data);
      return true;
    } catch (err: unknown) {
      // Cloud Function HttpsError: user-friendly `message` field is set by the
      // moderation pipeline (e.g. "Phone numbers can't be shared..."). Toast it.
      const e = err as { code?: string; message?: string };
      const friendly = e.message || 'Failed to send message';
      console.error('Failed to send message:', err);
      setError(friendly);
      toast.error(friendly);
      return false;
    } finally {
      setIsSending(false);
    }
  }, [user]);

  const markAsRead = useCallback(async (threadId: string): Promise<void> => {
    if (!user) return;
    try {
      const threadRef = doc(db, 'messageThreads', threadId);
      await updateDoc(threadRef, {
        [`unreadCounts.${user.uid}`]: 0,
        unreadCount: 0,
      });
    } catch (err) {
      console.error('Failed to mark as read:', err);
    }
  }, [user]);

  const archiveThread = useCallback(async (threadId: string): Promise<boolean> => {
    try {
      const threadRef = doc(db, 'messageThreads', threadId);
      await updateDoc(threadRef, { isArchived: true });
      return true;
    } catch (err) {
      setError('Failed to archive thread');
      return false;
    }
  }, []);

  const muteThread = useCallback(async (threadId: string, muted: boolean): Promise<boolean> => {
    try {
      const threadRef = doc(db, 'messageThreads', threadId);
      await updateDoc(threadRef, { isMuted: muted });
      return true;
    } catch (err) {
      setError('Failed to update mute status');
      return false;
    }
  }, []);

  return {
    sendMessage,
    markAsRead,
    archiveThread,
    muteThread,
    isSending,
    error,
  };
};

export default useSendMessage;
